import React from 'react';

import { formatDate, formatAmount, formatSignedAmount } from '../../utils';

// The body of the <UserLink> hover card. Deliberately presentational and free of
// redux/router so it can be rendered into the popover's detached subtree without
// depending on context reaching it — UserLink owns the data and the copy action
// and hands both down.

const initialsOf = (summary) => {
  const source = summary.name || summary.email || '';
  const parts = source
    .trim()
    .split(/[\s@.]+/)
    .filter(Boolean);

  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return (parts[0][0] + parts[1][0]).toUpperCase();
};

// A field is only "missing" once the fetch has settled. While it is in flight we
// show a skeleton instead of a dash, so an empty-looking card can't be mistaken
// for a user with no phone number on file.
function Field({ label, value, loading, copyable, onCopy }) {
  return (
    <div className="usc-field">
      <span className="usc-field-label">{label}</span>
      {value ? (
        <span className="usc-field-value" title={value}>
          {value}
          {copyable && (
            <i
              className="now-ui-icons files_single-copy-04 usc-copy"
              title={`Copy ${label.toLowerCase()}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onCopy(value, label);
              }}
            />
          )}
        </span>
      ) : loading ? (
        <span className="usc-skeleton" />
      ) : (
        <span className="usc-field-value usc-muted">—</span>
      )}
    </div>
  );
}

function UserSummaryCard({ summary, loading, failed, profileUrl, onCopy }) {
  const hasWallet = summary.balance !== null && summary.balance !== undefined;
  const debt = Number(summary.debt) || 0;

  return (
    <div className="user-summary-card">
      <div className="usc-header">
        <span className="usc-avatar">{initialsOf(summary)}</span>
        <span className="usc-identity">
          <span className="usc-name">{summary.name || 'Unnamed user'}</span>
          {/* Orders seed the card from a phone number, so the id only shows up
              once the record itself has loaded. */}
          {summary.id && <span className="usc-id">ID {summary.id}</span>}
        </span>
      </div>

      {(summary.deletedAt || summary.vendBlocked) && (
        <div className="usc-badges">
          {summary.deletedAt && <span className="usc-badge">Deleted</span>}
          {summary.vendBlocked && (
            <span className="usc-badge usc-badge-warn">Vend disabled</span>
          )}
        </div>
      )}

      <div className="usc-fields">
        <Field
          label="Phone"
          value={summary.phone}
          loading={loading}
          copyable
          onCopy={onCopy}
        />
        <Field
          label="Email"
          value={summary.email}
          loading={loading}
          copyable
          onCopy={onCopy}
        />
        <Field
          label="Joined"
          value={summary.joinedAt ? formatDate(summary.joinedAt) : null}
          loading={loading}
        />
      </div>

      <div className="usc-money">
        <span className="usc-money-cell">
          <span className="usc-field-label">Balance</span>
          {hasWallet ? (
            // A wallet drawing on its overdraft facility sits below zero, and
            // that negative IS the debt — render it signed.
            <span
              className={
                summary.balance < 0 ? 'usc-amount text-danger' : 'usc-amount'
              }
            >
              {formatSignedAmount(summary.balance)}
            </span>
          ) : loading ? (
            <span className="usc-skeleton" />
          ) : (
            <span className="usc-amount usc-muted">—</span>
          )}
        </span>

        {debt > 0 && (
          <span className="usc-money-cell">
            <span className="usc-field-label">Debt</span>
            <span className="usc-amount text-danger">{formatAmount(debt)}</span>
          </span>
        )}
      </div>

      {failed && (
        <p className="usc-error">
          Couldn’t load the full record — showing what we have.
        </p>
      )}

      <a
        className="usc-open"
        href={profileUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        Open profile ↗
      </a>
    </div>
  );
}

export default UserSummaryCard;
